import { Pencil, ScrollText } from 'lucide-react';

import { AVISO_DE_ACAO_INDISPONIVEL } from '@/modulos/operacao/comum/cadastroIndisponivel';
import { avisarIndisponivel } from '@/modulos/portal/credenciamento/indisponivel';
import { formatarData } from '@/nucleo/formato/datas';
import { mascaraCpf, mascaraTelefone } from '@/nucleo/formato/documentos';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';

import { usePortalAccess } from '../comum/usePortalAccess';
import { useRepresentantesLegais } from './dados';

interface RepresentantesLegaisCardProps {
  /** Quem administra a empresa credenciada — só essa pessoa vê "Solicitar alteração". */
  podeGerenciar: boolean;
}

/**
 * Card "Representantes Legais" do protótipo — espelho só de leitura do
 * cadastro oficial do credenciado.
 *
 * NADA GRAVA. A tabela `representantes` existe, mas não há rota que a sirva ao
 * portal nem fluxo de solicitação de alteração em `apps/api/src/modulos/`:
 * "Solicitar alteração" mostra o aviso de indisponibilidade, pelo `sonner`: o
 * `<Toaster />` montado em `App.tsx` é o dele, e um `useToast()` do shadcn não
 * apareceria na tela.
 *
 * Só para PJ e cooperativa, como no protótipo (`RepresentantesLegaisCard.tsx`,
 * `tipo !== "PJ" && tipo !== "COOPERATIVA"`): pessoa física não tem
 * representante legal.
 */
export function RepresentantesLegaisCard({ podeGerenciar }: RepresentantesLegaisCardProps) {
  const { isColetivo } = usePortalAccess();
  const { data: representantes, isLoading, isError, refetch } = useRepresentantesLegais();
  const ativos = (representantes ?? []).filter((representante) => representante.ativo).length;

  if (!isColetivo) return null;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1.5">
          <CardTitle asChild>
            <h2 className="flex flex-wrap items-center gap-2">
              <ScrollText className="h-4 w-4" aria-hidden="true" />
              Representantes Legais
              {representantes && (
                <Badge variant="outline" className="font-normal">
                  {ativos} ativo(s)
                </Badge>
              )}
            </h2>
          </CardTitle>
          <CardDescription>
            Os dados abaixo refletem o cadastro oficial do credenciado junto à DTP. Para incluir,
            substituir ou desativar um representante, abra uma solicitação — a alteração só passa a
            valer após análise interna.
          </CardDescription>
        </div>
        {podeGerenciar && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => avisarIndisponivel(AVISO_DE_ACAO_INDISPONIVEL)}
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
            Solicitar alteração
          </Button>
        )}
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <Spinner size="sm" />
        ) : isError ? (
          <ErrorState
            variant="load-failed"
            description="Não foi possível carregar os representantes legais agora. Tente novamente em instantes."
            primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
          />
        ) : !representantes || representantes.length === 0 ? (
          <EmptyState
            icon={ScrollText}
            title="Nenhum representante legal"
            description="Os representantes do cadastro oficial do credenciado aparecem aqui."
          />
        ) : (
          <Table aria-label="Representantes legais">
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>CPF</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Contato</TableHead>
                <TableHead>Vigência</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {representantes.map((representante) => (
                <TableRow key={representante.id}>
                  <TableCell className="font-medium">{representante.nome}</TableCell>
                  <TableCell className="whitespace-nowrap font-mono text-muted-foreground">
                    {mascaraCpf(representante.cpf)}
                  </TableCell>
                  <TableCell>{representante.cargo}</TableCell>
                  <TableCell>
                    <div className="text-muted-foreground">{representante.email}</div>
                    <div className="whitespace-nowrap text-xs text-muted-foreground">
                      {mascaraTelefone(representante.telefone)}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {formatarData(representante.vigencia_desde)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={representante.ativo ? 'default' : 'outline'}>
                      {representante.ativo ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
